import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];

const stripUnits = (raw) =>
  String(raw || "")
    .toLowerCase()
    .replace(/^1\s*:\s*/, "")
    .replace(/[^\d.,/-]+/g, "")
    .trim();

const parseUserNumber = (raw) => parseFraction(stripUnits(raw));

function ScaleInput({ userAnswer, onChange, disabled, label, suffixLatex, placeholder }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) =>
        onChange({
          raw: value?.raw ?? "",
          parsed: parseUserNumber(value?.raw ?? ""),
        })
      }
      disabled={disabled}
      label={label}
      suffixLatex={suffixLatex}
      placeholder={placeholder}
    />
  );
}

const taskRegistry = {
  PlanActualLengthTask: {
    generate() {
      return {
        data: pick([
          { objectGen: "pokoju", scale: 50, cm: 8, m: 4 },
          { objectGen: "ogrodu", scale: 100, cm: 12, m: 12 },
          { objectGen: "boiska", scale: 200, cm: 9, m: 18 },
          { objectGen: "sali gimnastycznej", scale: 250, cm: 6, m: 15 },
          { objectGen: "działki", scale: 500, cm: 5, m: 25 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Na planie w skali <InlineMath math={`1:${data.scale}`} /> długość {data.objectGen} wynosi <InlineMath math={`${data.cm}\\,\\text{cm}`} />.
          Oblicz rzeczywistą długość {data.objectGen} w metrach.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <ScaleInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="d" suffixLatex={String.raw`\text{m}`} placeholder="np. 15" />;
    },
    answer: ({ data }) => data.m,
    answerLabel: "d",
    answerSuffix: "\\,\\mathrm{m}",
    renderAnswer: ({ data }) => <InlineMath math={`d=${data.m}\\,\\text{m}`} />,
    renderExplanation({ data }) {
      const realCm = data.cm * data.scale;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Mnożymy długość z planu przez mianownik skali.</p>
          <BlockMath math={`${data.cm}\\cdot ${data.scale}=${realCm}\\,\\text{cm}`} />
          <p><strong>Krok 2.</strong> Zamieniamy centymetry na metry.</p>
          <BlockMath math={`${realCm}\\,\\text{cm}=${data.m}\\,\\text{m}`} />
        </div>
      );
    },
  },

  MapScaleRealDistanceTask: {
    generate() {
      return {
        data: pick([
          { scale: 25000, cm: 4, km: 1 },
          { scale: 50000, cm: 6, km: 3 },
          { scale: 100000, cm: 5, km: 5 },
          { scale: 200000, cm: 7, km: 14 },
          { scale: 500000, cm: 3, km: 15 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Na mapie w skali <InlineMath math={`1:${data.scale}`} /> odległość między
          miejscowościami wynosi <InlineMath math={`${data.cm}\\,\\text{cm}`} />.
          Oblicz rzeczywistą odległość między tymi miejscowościami w kilometrach.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <ScaleInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="d" suffixLatex={String.raw`\text{km}`} placeholder="np. 12" />;
    },
    answer: ({ data }) => data.km,
    answerLabel: "d",
    answerSuffix: "\\,\\mathrm{km}",
    renderAnswer: ({ data }) => <InlineMath math={`d=${data.km}\\,\\text{km}`} />,
    renderExplanation({ data }) {
      const realCm = data.cm * data.scale;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Skala <InlineMath math={`1:${data.scale}`} /> oznacza, że <InlineMath math="1\\,\\text{cm}" /> na mapie odpowiada <InlineMath math={`${data.scale}\\,\\text{cm}`} /> w rzeczywistości.</p>
          <BlockMath math={`${data.cm}\\cdot ${data.scale}=${realCm}\\,\\text{cm}`} />
          <p><strong>Krok 2.</strong> Zamieniamy centymetry na kilometry.</p>
          <BlockMath math={`${realCm}\\,\\text{cm}=${data.km}\\,\\text{km}`} />
        </div>
      );
    },
  },

  MapScaleMapDistanceTask: {
    generate() {
      return {
        data: pick([
          { scale: 25000, km: 2, cm: 8 },
          { scale: 50000, km: 4, cm: 8 },
          { scale: 100000, km: 7, cm: 7 },
          { scale: 200000, km: 10, cm: 5 },
          { scale: 500000, km: 20, cm: 4 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Rzeczywista odległość między dwoma punktami wynosi <InlineMath math={`${data.km}\\,\\text{km}`} />.
          Mapa jest wykonana w skali <InlineMath math={`1:${data.scale}`} />.
          Oblicz, ile centymetrów ma ta odległość na mapie.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <ScaleInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="d_m" suffixLatex={String.raw`\text{cm}`} placeholder="np. 6" />;
    },
    answer: ({ data }) => data.cm,
    answerLabel: "d_m",
    answerSuffix: "\\,\\mathrm{cm}",
    renderAnswer: ({ data }) => <InlineMath math={`d_m=${data.cm}\\,\\text{cm}`} />,
    renderExplanation({ data }) {
      const realCm = data.km * 100000;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Zamieniamy kilometry na centymetry.</p>
          <BlockMath math={`${data.km}\\,\\text{km}=${realCm}\\,\\text{cm}`} />
          <p><strong>Krok 2.</strong> W skali <InlineMath math={`1:${data.scale}`} /> długość na mapie jest <InlineMath math={`${data.scale}`} /> razy mniejsza.</p>
          <BlockMath math={`d_m=\\frac{${realCm}}{${data.scale}}=${data.cm}\\,\\text{cm}`} />
        </div>
      );
    },
  },

  FindMapScaleTask: {
    generate() {
      return {
        data: pick([
          { cm: 4, km: 2, scale: 50000 },
          { cm: 5, km: 5, scale: 100000 },
          { cm: 8, km: 16, scale: 200000 },
          { cm: 3, km: 15, scale: 500000 },
          { cm: 12, km: 3, scale: 25000 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Na mapie odcinek o długości <InlineMath math={`${data.cm}\\,\\text{cm}`} /> odpowiada w rzeczywistości odległości <InlineMath math={`${data.km}\\,\\text{km}`} />.
          Wyznacz skalę tej mapy w postaci <InlineMath math="1:n" />. Napisz, ile wynosi <InlineMath math="n" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <ScaleInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="n" placeholder="np. 50000" />;
    },
    answer: ({ data }) => data.scale,
    answerLabel: "n",
    answerSuffix: "",
    renderAnswer: ({ data }) => <InlineMath math={`1:${data.scale}`} />,
    renderExplanation({ data }) {
      const realCm = data.km * 100000;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Sprowadzamy obie długości do centymetrów.</p>
          <BlockMath math={`${data.km}\\,\\text{km}=${realCm}\\,\\text{cm}`} />
          <p><strong>Krok 2.</strong> Sprawdzamy, ile centymetrów w rzeczywistości przypada na <InlineMath math="1\\,\\text{cm}" /> na mapie.</p>
          <BlockMath math={`n=\\frac{${realCm}}{${data.cm}}=${data.scale}`} />
          <BlockMath math={`\\text{skala}=1:${data.scale}`} />
        </div>
      );
    },
  },

  RectangularPlanDimensionsTask: {
    generate() {
      return {
        data: pick([
          { objectNom: "prostokątny pokój", scale: 50, aCm: 8, bCm: 6, aM: 4, bM: 3 },
          { objectNom: "prostokątny ogród", scale: 100, aCm: 12, bCm: 8, aM: 12, bM: 8 },
          { objectNom: "prostokątna działka", scale: 200, aCm: 15, bCm: 10, aM: 30, bM: 20 },
          { objectNom: "prostokątne boisko", scale: 500, aCm: 12, bCm: 6, aM: 60, bM: 30 },
          { objectNom: "prostokątny plac zabaw", scale: 100, aCm: 9, bCm: 7, aM: 9, bM: 7 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Na planie w skali <InlineMath math={`1:${data.scale}`} /> {data.objectNom} ma wymiary <InlineMath math={`a=${data.aCm}\\,\\text{cm}`} /> oraz <InlineMath math={`b=${data.bCm}\\,\\text{cm}`} />.
          Oblicz rzeczywiste wymiary tego obiektu w metrach.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      const ua = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
      const setField = (field) => (value) =>
        onChange({
          ...ua,
          [field]: {
            raw: value?.raw ?? "",
            parsed: parseUserNumber(value?.raw ?? ""),
          },
        });
      return (
        <div style={{ display: "grid", gap: 14 }}>
          <FractionInput value={ua.a?.raw ?? ""} onChange={setField("a")} disabled={disabled} label="a" suffixLatex={String.raw`\text{m}`} placeholder="np. 12" />
          <FractionInput value={ua.b?.raw ?? ""} onChange={setField("b")} disabled={disabled} label="b" suffixLatex={String.raw`\text{m}`} placeholder="np. 8" />
        </div>
      );
    },
    checkAnswer({ data }, userAnswer) {
      const ua = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
      return Math.abs((ua.a?.parsed ?? NaN) - data.aM) < 0.01 && Math.abs((ua.b?.parsed ?? NaN) - data.bM) < 0.01;
    },
    renderAnswer: ({ data }) => <InlineMath math={`a=${data.aM}\\,\\text{m},\\quad b=${data.bM}\\,\\text{m}`} />,
    renderUserAnswer(userAnswer) {
      const ua = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
      const a = ua.a?.raw ? formatLatexPreview(ua.a.raw, "a") + String.raw`\,\mathrm{m}` : "a=\\text{brak}";
      const b = ua.b?.raw ? formatLatexPreview(ua.b.raw, "b") + String.raw`\,\mathrm{m}` : "b=\\text{brak}";
      return <InlineMath math={`${a},\\quad ${b}`} />;
    },
    renderExplanation({ data }) {
      const realACm = data.aCm * data.scale;
      const realBCm = data.bCm * data.scale;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Każdy wymiar z planu mnożymy przez mianownik skali.</p>
          <BlockMath math={`a=${data.aCm}\\cdot ${data.scale}=${realACm}\\,\\text{cm}`} />
          <BlockMath math={`b=${data.bCm}\\cdot ${data.scale}=${realBCm}\\,\\text{cm}`} />
          <p><strong>Krok 2.</strong> Zamieniamy centymetry na metry.</p>
          <BlockMath math={`${realACm}\\,\\text{cm}=${data.aM}\\,\\text{m}`} />
          <BlockMath math={`${realBCm}\\,\\text{cm}=${data.bM}\\,\\text{m}`} />
        </div>
      );
    },
  },

};

function moduleFor(task) {
  const mod = taskRegistry[task?.type];
  if (!mod) throw new Error(`Nieznany typ zadania skali: ${task?.type}`);
  return mod;
}

export function generateTask(type) {
  const taskType = type || pick(Object.keys(taskRegistry));
  return { type: taskType, ...taskRegistry[taskType].generate() };
}

const taskOrder = [
  "PlanActualLengthTask",
  "MapScaleRealDistanceTask",
  "MapScaleMapDistanceTask",
  "FindMapScaleTask",
  "RectangularPlanDimensionsTask",
];

export function generateTasks() {
  return taskOrder.map((type) => generateTask(type));
}

export const skalaRegistry = Object.fromEntries(
  taskOrder.map((type) => [
    type,
    {
      renderTask: (task) => renderTask({ ...task, type }),
      renderInput: (task, userAnswer, onChange, disabled) =>
        renderInput({ ...task, type }, userAnswer, onChange, disabled),
      checkAnswer: (task, userAnswer) => checkAnswer({ ...task, type }, userAnswer),
      renderAnswer: (task) => renderAnswer({ ...task, type }),
      renderExplanation: (payload) => renderExplanation({ ...payload, type }),
      renderUserAnswer: (userAnswer, task) => renderUserAnswer({ ...task, type }, userAnswer),
    },
  ])
);

export function renderTask(task) {
  return moduleFor(task).renderTask(task);
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return moduleFor(task).renderInput(task, userAnswer, onChange, disabled);
}

export function checkAnswer(task, userAnswer) {
  const mod = moduleFor(task);
  if (mod.checkAnswer) return mod.checkAnswer(task, userAnswer);
  const expected = mod.answer(task);
  const parsed =
    typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
      ? userAnswer.parsed
      : parseUserNumber(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return Number.isFinite(parsed) && Math.abs(parsed - expected) < 0.01;
}

export function renderAnswer(task) {
  return moduleFor(task).renderAnswer(task);
}

export function renderUserAnswer(task, userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const mod = moduleFor(task);
  if (mod.renderUserAnswer) return mod.renderUserAnswer(userAnswer, task);
  return <InlineMath math={`${formatLatexPreview(raw, mod.answerLabel)}${mod.answerSuffix}`} />;
}

export function renderExplanation(task) {
  return moduleFor(task).renderExplanation(task);
}

export const registry = skalaRegistry;

